/**
 * Scribe (outils.scribe, lots 1c et 2) – fournisseur de l'enregistreur de réunions, monté par la coquille comme le
 * softphone : l'enregistrement continue quand on change d'écran ou de page. Il porte le moteur (enregistreur.ts :
 * MediaRecorder, parties de 10 s écrites dans IndexedDB puis déposées dans S3, séquences de 10 min, reprise après
 * rechargement), la prise de son (prise-de-son.ts : micro choisi, son des participants à distance, niveau) et les règles
 * de la coquille :
 *  - un seul onglet enregistre (verrou `agora:scribe`, useVerrouOnglet) : les autres ne proposent pas de reprendre sa
 *    réunion ; il diffuse son état sur le canal `agora:scribe` (onglets.ts) et les autres onglets le montrent (bouton
 *    rouge du dock, titre, durée et état dans le panneau) et peuvent lui demander de s'arrêter ; sans ce canal,
 *    « Scribe enregistre dans un autre onglet ». Onglet qui enregistre fermé : verrou libéré, les autres le voient
 *    aussitôt (demande partagée du verrou en attente) et proposent la réunion à reprendre ;
 *  - un enregistrement (ou l'envoi de ses dernières parties) fait confirmer la fermeture de l'onglet et toute sortie
 *    d'AGORA (useActiviteEnCours('enregistrement')) ;
 *  - l'écran reste allumé pendant l'enregistrement, onglet visible (Screen Wake Lock) ;
 *  - le titre de l'onglet commence par « ● » (TitreDocument de la coquille, PREFIXE_ENREGISTREMENT).
 * Lot 2 : pause sur silence (60 s sans son → MediaRecorder en pause, reprise au premier son ; DetecteurSilence) ; micro
 * changé pendant l'enregistrement (choix dans le panneau, micro débranché : `devicechange` et piste terminée → le micro
 * choisi s'il est encore là, sinon celui par défaut, dans une nouvelle séquence) ; son des participants à distance
 * (partage de l'onglet ou de l'écran de Teams avec son audio, mêlé au micro ; Edge et Chrome ; partage terminé → le micro
 * continue seul, avis ; après un rechargement, le partage est à refaire).
 * Actif pour qui a les droits « lire » et « creer » sur outils.scribe ; le bouton du dock apparaît avec l'option des
 * Réglages (préférence `scribe`) et reste affiché tant qu'une réunion est en cours. Panneau : PanneauScribe.tsx.
 * Mode maquette : mêmes appels, simulés par core/api/maquette-scribe.ts (dépôt S3 court-circuité, URL « maquette: »).
 */
import React from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { RESSOURCE_SCRIBE, type Reunion, type ReunionNouvelle, type Televersement } from '@agora/shared';
import { ErreurApi } from '../api/client';
import { useSocle } from '../contexte';
import { useActiviteEnCours } from '../pages/sortie';
import { useVerrouOnglet } from '../pages/verrou-onglet';
import { ETAT_INITIAL, MoteurScribe, formatAudio, type EtatScribe, type FormatAudio, type Magnetophone, type Refus } from './enregistreur';
import { lireMessage, ouvrirCanal, resumerEtat, type MessageOnglets } from './onglets';
import { DetecteurSilence, ErreurPartage, MESURE_SILENCE_MS, PriseDeSon, partageSonPossible, partagerSon } from './prise-de-son';
import { ouvrirStockage } from './stockage';

/** Verrou entre les onglets d'AGORA (Web Locks) : un seul enregistre. */
export const VERROU_SCRIBE = 'agora:scribe';
/** Ouvre le panneau Scribe du dock (écran Outils › Scribe, « Démarrer une réunion ») : DockAgora l'écoute. */
export const EVENEMENT_SCRIBE = 'agora:scribe';
export const ouvrirPanneauScribe = () => window.dispatchEvent(new Event(EVENEMENT_SCRIBE));
/** Début du titre de l'onglet pendant un enregistrement. */
export const PREFIXE_ENREGISTREMENT = '● ';
/** Débit de l'Opus : 32 kbit/s, soit ≈ 40 Ko par partie de 10 s (plafond d'une partie : 1 Mo). */
const DEBIT_AUDIO = 32_000;
/** Vérification du verrou tenu par un autre onglet (onglet visible, pas d'enregistrement ici). */
const VERIFICATION_VERROU_MS = 3000;

export type ErreurMicro = 'refuse' | 'indisponible';
export interface MicroScribe {
  /** Flux du micro choisi (niveau avant le démarrage, puis enregistrement). */
  flux: MediaStream | null;
  appareils: { id: string; libelle: string }[];
  choisi: string;
  erreur: ErreurMicro | null;
  /** Ouvre le micro `id` (par défaut : celui du système) ; pendant l'enregistrement, nouvelle séquence sur ce micro. */
  ouvrir: (id?: string) => Promise<MediaStream | null>;
  /** Rend le micro (sans effet pendant un enregistrement). */
  fermer: () => void;
}

/** Refus d'un partage du son de Teams : fenêtre fermée ou refusée, son non partagé, mélange impossible. */
export type ErreurPartageScribe = 'refuse' | 'sansSon' | 'melange';

/** Son des participants à distance (partage de l'onglet ou de l'écran de Teams avec son audio). */
export interface DistanceScribe {
  /** Option « Participants à distance » choisie. */
  actif: boolean;
  /** Son partagé (et qui arrive encore). */
  partage: boolean;
  erreur: ErreurPartageScribe | null;
  activer: (on: boolean) => void;
  /** Demande le partage (dans le geste de l'utilisateur) ; pendant l'enregistrement, le son rejoint le mélange. */
  partager: () => Promise<void>;
  /** Arrête le partage (le micro continue seul). */
  arreter: () => void;
}

/** Avis de la prise de son : partage de Teams terminé, micro changé, plus de micro (son de Teams seul). */
export type AvisSonScribe = 'ecranCoupe' | 'microChange' | 'sansMicro';

export interface ContexteScribe {
  etat: EtatScribe;
  /** Un autre onglet d'AGORA enregistre (verrou tenu ailleurs). */
  ailleurs: boolean;
  /**
   * Enregistrement de l'autre onglet, diffusé par celui-ci (phase `enregistrement` ou `envoi`, réunion, durée, pause,
   * hors ligne) ; null si cet onglet enregistre, si personne n'enregistre ou si l'état n'est pas connu.
   */
  autreOnglet: EtatScribe | null;
  /** Demande à l'onglet qui enregistre d'arrêter (comme son bouton « Arrêter l'enregistrement »). */
  arreterAutreOnglet: () => void;
  /** Format Opus du navigateur ; null : pas d'enregistrement possible (message). */
  format: FormatAudio | null;
  /** Stockage du navigateur persistant (reprise possible après un rechargement). */
  persistant: boolean;
  micro: MicroScribe;
  /** Son des participants à distance ; null : navigateur qui ne le partage pas (option masquée). */
  distance: DistanceScribe | null;
  avisSon: AvisSonScribe | null;
  /** Crée la réunion (POST …/reunions) puis enregistre ; rejette avec l'ErreurApi de l'API (plafond…). */
  demarrer: (corps: ReunionNouvelle) => Promise<Reunion>;
  /**
   * Durée maximale atteinte : démarre la suite (nouvelle réunion) ; si la réunion arrêtée enregistrait le son de Teams,
   * le partage est d'abord demandé de nouveau (à appeler dans le geste de l'utilisateur). Rejette comme `demarrer`, ou
   * avec l'ErreurPartage d'un partage refusé.
   */
  relancer: (corps: ReunionNouvelle) => Promise<Reunion>;
  arreter: () => void;
  /** Pause sur silence : reprend l'enregistrement tout de suite (« Reprendre maintenant »). */
  reprendreSon: () => void;
  /** Réunion inachevée : nouvelle séquence (si elle enregistre encore sur le serveur). */
  reprendre: () => Promise<void>;
  /** Réunion inachevée : dépôt de ce qui reste, puis fin. */
  terminer: () => void;
  /** Après le traitement ou un avis : prêt pour une nouvelle réunion. */
  nouvelle: () => void;
}

const Ctx = React.createContext<ContexteScribe | null>(null);

/** Enregistreur Scribe de la coquille, ou null (pas de droit sur l'outil). */
export function useScribe(): ContexteScribe | null {
  return React.useContext(Ctx);
}

/** Décision du moteur selon l'erreur d'un dépôt ou de la fin. */
export function classerErreur(e: unknown): Refus {
  if (!(e instanceof ErreurApi)) return 'reessayer';
  if (e.code === 'quota_scribe_atteint') return 'quota';
  if (e.code === 'etat_incompatible' || e.code === 'introuvable' || e.code === 'interdit' || e.code === 'module_inactif') return 'reunion';
  if (e.code === 'validation') return 'partie';
  return 'reessayer';
}

/** MediaRecorder réduit à ce que le moteur utilise. */
function magnetophone(flux: MediaStream, mime: string): Magnetophone {
  const r = new MediaRecorder(flux, { mimeType: mime, audioBitsPerSecond: DEBIT_AUDIO });
  const m: Magnetophone = {
    get state() { return r.state; },
    start: (ms) => r.start(ms),
    stop: () => r.stop(),
    pause: () => r.pause(),
    resume: () => r.resume(),
    ondataavailable: null,
    onstop: null,
  };
  r.ondataavailable = (e) => m.ondataavailable?.({ data: e.data });
  r.onstop = () => m.onstop?.();
  return m;
}

/**
 * Verrou Scribe tenu (par un autre onglet, ou par celui-ci s'il enregistre). Seule la demande exclusive de l'onglet qui
 * enregistre compte : les demandes partagées des autres onglets (guet de la fin) ne font qu'attendre.
 */
async function verrouTenu(): Promise<boolean> {
  try {
    const etat = await navigator.locks?.query();
    return !!etat?.held?.some((v) => v.name === VERROU_SCRIBE && v.mode !== 'shared');
  } catch {
    return false;
  }
}

const arreterFlux = (f: MediaStream | null | undefined) => f?.getTracks().forEach((p) => p.stop());
const pisteVivante = (f: MediaStream | null | undefined) => !!f?.getAudioTracks().some((p) => p.readyState === 'live');

export function FournisseurScribe({ children }: { children: React.ReactNode }) {
  const { api, moi, noeuds, config } = useSocle();
  const queryClient = useQueryClient();
  const droits = noeuds.get(RESSOURCE_SCRIBE)?.droits;
  // Droit acquis une fois pour toutes : changer de filiale ne coupe pas un enregistrement.
  const [autorise, setAutorise] = React.useState(false);
  React.useEffect(() => { if (droits?.lire && droits.creer) setAutorise(true); }, [droits?.lire, droits?.creer]);

  const contexte = React.useRef({ api, queryClient, maquette: config.maquette });
  contexte.current = { api, queryClient, maquette: config.maquette };
  /** Prise de son de l'enregistrement en cours (null hors enregistrement). */
  const capture = React.useRef<PriseDeSon | null>(null);
  /** Remplaçant d'un micro perdu (défini plus bas : il dépend du micro choisi). */
  const remplacer = React.useRef<() => Promise<MediaStream | null>>(async () => null);

  const [moteur] = React.useState(() => new MoteurScribe({
    stockage: ouvrirStockage,
    creerMagnetophone: magnetophone,
    classer: classerErreur,
    deposer: async (p) => {
      const { api: client } = contexte.current;
      const t = await client.post<Televersement>(`/v1/outils/scribe/reunions/${encodeURIComponent(p.reunion)}/parties`, {
        sequence: p.sequence, numero: p.numero, type: p.type, taille: p.audio.size, dureeMs: p.dureeMs,
      });
      // Maquette : rien à déposer (aucun appel réseau).
      if (t.url.startsWith('maquette:')) return;
      const corps = new FormData();
      for (const [k, v] of Object.entries(t.champs)) corps.append(k, v);
      corps.append('file', p.audio);   // le fichier en DERNIER (S3 ignore les champs placés après)
      const r = await fetch(t.url, { method: 'POST', body: corps });
      if (!r.ok) throw new Error(`S3 ${r.status}`);
    },
    terminer: async (reunion) => {
      try {
        await contexte.current.api.post<Reunion>(`/v1/outils/scribe/reunions/${encodeURIComponent(reunion)}/fin`, {});
      } catch (e) {
        // Déjà arrêtée (fin reçue mais réponse perdue, réunion abandonnée arrêtée par le serveur) : c'est fait.
        if (e instanceof ErreurApi && e.code === 'etat_incompatible') return;
        throw e;
      }
    },
    apresFin: () => { void contexte.current.queryClient.invalidateQueries({ queryKey: ['scribe'] }); },
    remplacerFlux: () => remplacer.current(),
  }));
  const [etat, setEtat] = React.useState<EtatScribe>(ETAT_INITIAL);
  React.useEffect(() => moteur.abonner(setEtat), [moteur]);
  React.useEffect(() => () => { moteur.liberer(); capture.current?.fermer(true); capture.current = null; }, [moteur]);
  const [format] = React.useState(() => formatAudio(typeof MediaRecorder === 'undefined' ? undefined : (m) => MediaRecorder.isTypeSupported(m)));
  const [partagePossible] = React.useState(partageSonPossible);

  const enregistre = etat.phase === 'enregistrement' || etat.phase === 'envoi';
  const verrou = useVerrouOnglet(VERROU_SCRIBE, autorise && enregistre);
  useActiviteEnCours('enregistrement', enregistre);

  // ---------------------------------------------------------------- autres onglets (onglets.ts)
  // L'onglet qui enregistre (il tient le verrou) diffuse son état sur le canal `agora:scribe` ; les autres l'affichent
  // et peuvent lui demander d'arrêter. Rien d'autre ne passe d'un onglet à l'autre (ni micro, ni réunion).
  const proprietaire = enregistre && verrou.etat === 'detenu';
  const canal = React.useRef<BroadcastChannel | null>(null);
  const poster = React.useCallback((m: MessageOnglets) => {
    try { canal.current?.postMessage(m); } catch { /* canal fermé */ }
  }, []);
  const [autreOnglet, setAutreOnglet] = React.useState<EtatScribe | null>(null);
  /** Incrémenté à la fin d'un enregistrement ailleurs : verrou vérifié de nouveau tout de suite. */
  const [verification, setVerification] = React.useState(0);
  const reception = React.useRef<(brut: unknown) => void>(() => undefined);
  reception.current = (brut) => {
    const m = lireMessage(brut);
    if (!m) return;
    if (m.type === 'demande') {
      if (proprietaire) poster({ type: 'etat', etat: resumerEtat(moteur.lire()) });
      return;
    }
    if (m.type === 'arreter') {
      // Comme un clic sur « Arrêter l'enregistrement » dans cet onglet, et seulement pour sa propre réunion.
      const e = moteur.lire();
      if (proprietaire && e.phase === 'enregistrement' && e.reunion?.id === m.reunion) void moteur.arreter();
      return;
    }
    if (enregistre) return;
    if (m.type === 'etat') setAutreOnglet(m.etat);
    else { setAutreOnglet(null); setVerification((n) => n + 1); }
  };
  React.useEffect(() => {
    if (!autorise) return;
    const c = ouvrirCanal();
    if (!c) return;
    canal.current = c;
    c.onmessage = (e: MessageEvent) => reception.current(e.data);
    return () => { c.close(); if (canal.current === c) canal.current = null; };
  }, [autorise]);
  // Onglet qui enregistre : état diffusé à chaque changement (au plus une fois par partie de 10 s, pause, reprise…) ;
  // enregistrement terminé ici, fournisseur démonté ou onglet quitté : « fin ».
  React.useEffect(() => { if (proprietaire) poster({ type: 'etat', etat: resumerEtat(etat) }); }, [proprietaire, etat, poster]);
  React.useEffect(() => {
    if (!proprietaire) return;
    const partir = () => poster({ type: 'fin' });
    window.addEventListener('pagehide', partir);
    return () => { window.removeEventListener('pagehide', partir); partir(); };
  }, [proprietaire, poster]);

  // Autre onglet qui enregistre : vérifié au chargement, toutes les 3 s (onglet visible) et dès que le verrou est libéré
  // (demande partagée du verrou, en attente tant qu'il est tenu ailleurs : accordée quand l'onglet qui enregistre
  // s'arrête ou se ferme, puis rendue aussitôt) ; verrou tenu → état demandé à l'onglet qui enregistre ; verrou libéré
  // → son état est oublié et la réunion inachevée de l'utilisateur cherchée dans le stockage local.
  const [ailleurs, setAilleurs] = React.useState(false);
  React.useEffect(() => {
    if (!autorise || enregistre || verrou.etat === 'detenu') { setAilleurs(false); setAutreOnglet(null); return; }
    let fini = false;
    let avant: boolean | null = null;
    let guet: AbortController | null = null;
    const verifier = async () => {
      const tenu = await verrouTenu();
      if (fini) return;
      setAilleurs(tenu);
      if (tenu) {
        guetter();
        if (avant !== true) poster({ type: 'demande' });
      } else {
        // Sans Web Locks, le verrou n'est jamais vu tenu : l'état diffusé reste jusqu'à la « fin ».
        if (navigator.locks) setAutreOnglet(null);
        if (avant !== false) void moteur.charger(moi.id);
      }
      avant = tenu;
    };
    const guetter = () => {
      const verrous = navigator.locks;
      if (guet || !verrous) return;
      const g = new AbortController();
      guet = g;
      // Mode partagé : n'empêche aucun onglet d'enregistrer ; tenu le temps d'un appel vide.
      verrous.request(VERROU_SCRIBE, { mode: 'shared', signal: g.signal }, () => undefined)
        .then(() => { if (guet === g) guet = null; if (!fini) void verifier(); })
        .catch(() => { if (guet === g) guet = null; });
    };
    void verifier();
    const id = window.setInterval(() => { if (document.visibilityState === 'visible') void verifier(); }, VERIFICATION_VERROU_MS);
    return () => { fini = true; window.clearInterval(id); guet?.abort(); };
  }, [autorise, enregistre, verrou.etat, moteur, moi.id, verification, poster]);

  // Retour du réseau : nouvelle tentative de dépôt tout de suite.
  React.useEffect(() => {
    const relancer = () => moteur.relancer();
    window.addEventListener('online', relancer);
    return () => window.removeEventListener('online', relancer);
  }, [moteur]);

  // Écran allumé pendant l'enregistrement, onglet visible (le navigateur rend le verrou quand l'onglet est masqué).
  React.useEffect(() => {
    if (etat.phase !== 'enregistrement' || !navigator.wakeLock) return;
    let fini = false;
    let sentinelle: WakeLockSentinel | null = null;
    const demander = () => {
      if (document.visibilityState !== 'visible') return;
      navigator.wakeLock.request('screen')
        .then((s) => { if (fini) void s.release(); else sentinelle = s; })
        .catch(() => undefined);
    };
    demander();
    document.addEventListener('visibilitychange', demander);
    return () => {
      fini = true;
      document.removeEventListener('visibilitychange', demander);
      void sentinelle?.release().catch(() => undefined);
    };
  }, [etat.phase]);

  const micro = useMicro(enregistre);
  const { ouvrir: ouvrirMicroLibre, ouvrirFlux, adopter, actualiser, abandonner: abandonnerFlux } = micro;
  const [avisSon, setAvisSon] = React.useState<AvisSonScribe | null>(null);

  // ---------------------------------------------------------------- son des participants à distance (Teams)
  const [distanceActive, setDistanceActive] = React.useState(false);
  const [partage, setPartage] = React.useState<MediaStream | null>(null);
  const partageRef = React.useRef<MediaStream | null>(null);
  partageRef.current = partage;
  const [erreurPartage, setErreurPartage] = React.useState<ErreurPartageScribe | null>(null);
  // Réunion à reprendre qui enregistrait le son de Teams : l'option revient cochée (le partage est à refaire).
  React.useEffect(() => { if (etat.phase === 'a-reprendre' && etat.reunion?.distance) setDistanceActive(true); }, [etat.phase, etat.reunion?.distance]);

  const arreterPartage = React.useCallback(() => {
    const enCours = capture.current;
    if (enCours && enCours.ecran === partageRef.current) enCours.retirerEcran();
    else arreterFlux(partageRef.current);
    setPartage(null);
  }, []);

  const partager = React.useCallback(async () => {
    setErreurPartage(null);
    let flux: MediaStream;
    try {
      flux = await partagerSon();
    } catch (e) {
      setErreurPartage(e instanceof ErreurPartage ? e.motif : 'refuse');
      return;
    }
    const enCours = capture.current;
    if (enCours && moteur.lire().phase === 'enregistrement') {
      // Pendant l'enregistrement : le son rejoint le mélange (créé au besoin : nouvelle séquence sur le mélange).
      const r = enCours.ajouterEcran(flux);
      if (!r.possible) { arreterFlux(flux); setErreurPartage('melange'); return; }
      if (r.nouveauFlux && enCours.sortie) await moteur.changerFlux(enCours.sortie, true);
      setAvisSon(null);
    } else {
      arreterFlux(partageRef.current);
    }
    setPartage(flux);
  }, [moteur]);

  // Partage terminé (« Arrêter le partage » du navigateur, onglet de Teams fermé) : le micro continue seul.
  React.useEffect(() => {
    const piste = partage?.getAudioTracks()[0];
    if (!piste) return;
    const fin = () => {
      if (partageRef.current !== partage) return;
      setPartage(null);
      if (capture.current?.ecran === partage) {
        capture.current.retirerEcran();
        if (moteur.lire().phase === 'enregistrement') setAvisSon('ecranCoupe');
      }
    };
    piste.addEventListener('ended', fin);
    return () => piste.removeEventListener('ended', fin);
  }, [partage, moteur]);

  // ---------------------------------------------------------------- micro pendant l'enregistrement
  /** Le micro de l'enregistrement change : nouvelle séquence, puis l'ancien micro est rendu. */
  const basculerMicro = React.useCallback(async (nouveau: MediaStream | null): Promise<MediaStream | null> => {
    const c = capture.current;
    if (!c) { arreterFlux(nouveau); return null; }
    c.changerMicro(nouveau);
    if (nouveau) adopter(nouveau);
    setAvisSon(nouveau ? 'microChange' : c.mixe ? 'sansMicro' : null);
    return c.sortie;
  }, [adopter]);

  // Micro perdu (moteur) : le micro choisi s'il est encore branché, sinon celui par défaut ; avec Teams, le mélange.
  remplacer.current = async () => {
    const c = capture.current;
    if (!c) return null;
    const ancien = c.micro;
    const appareils = await actualiser();
    const prefere = micro.prefere.current;
    const nouveau = await ouvrirFlux(prefere && appareils.some((a) => a.id === prefere) ? prefere : undefined, true);
    const sortie = await basculerMicro(nouveau);
    if (ancien !== nouveau) arreterFlux(ancien);
    return sortie;
  };

  const changerMicro = React.useCallback(async (id?: string): Promise<MediaStream | null> => {
    const c = capture.current;
    if (!c) return null;
    micro.prefere.current = id ?? '';
    const nouveau = await ouvrirFlux(id, true);
    if (!nouveau) return c.micro;
    const ancien = c.micro;
    const sortie = await basculerMicro(nouveau);
    if (sortie) await moteur.changerFlux(sortie);
    if (ancien !== nouveau) arreterFlux(ancien);
    return nouveau;
  }, [micro.prefere, ouvrirFlux, basculerMicro, moteur]);

  const ouvrirMicro = React.useCallback(
    (id?: string) => (capture.current && moteur.lire().phase === 'enregistrement' ? changerMicro(id) : ouvrirMicroLibre(id)),
    [changerMicro, ouvrirMicroLibre, moteur],
  );

  // Piste du micro terminée (débranché, coupé par le système) ou appareil retiré : remplacement par le moteur.
  React.useEffect(() => {
    if (etat.phase !== 'enregistrement') return;
    const piste = micro.etat.flux?.getAudioTracks()[0];
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    const fin = () => { void moteur.microPerdu(); };
    const changement = async () => {
      const appareils = await actualiser();
      const enCours = capture.current?.micro?.getAudioTracks()[0];
      const id = enCours?.getSettings?.().deviceId;
      if (!enCours ? appareils.length > 0 : enCours.readyState === 'ended' || (!!id && appareils.length > 0 && !appareils.some((a) => a.id === id))) {
        void moteur.microPerdu();
      }
    };
    piste?.addEventListener('ended', fin);
    md?.addEventListener?.('devicechange', changement);
    return () => {
      piste?.removeEventListener('ended', fin);
      md?.removeEventListener?.('devicechange', changement);
    };
  }, [etat.phase, micro.etat.flux, moteur, actualiser]);

  // Pause sur silence : niveau mesuré 4 fois par seconde ; 60 s de silence → pause ; premier son → reprise.
  React.useEffect(() => {
    if (etat.phase !== 'enregistrement') return;
    const detecteur = new DetecteurSilence();
    const id = window.setInterval(() => {
      const decision = detecteur.mesure(capture.current?.niveau() ?? null, Date.now(), moteur.lire().pause);
      if (decision === 'pause') moteur.mettreEnPause();
      else if (decision === 'reprise') moteur.reprendreSon();
    }, MESURE_SILENCE_MS);
    return () => window.clearInterval(id);
  }, [etat.phase, moteur]);

  // Fin de l'enregistrement : le moteur a rendu le flux enregistré ; micro, partage et mélange sont rendus aussi.
  React.useEffect(() => {
    if (etat.phase === 'enregistrement') return;
    if (capture.current) {
      capture.current.fermer(true);
      capture.current = null;
      setPartage(null);
    }
    setAvisSon(null);
    abandonnerFlux();
  }, [etat.phase, abandonnerFlux]);

  /**
   * Prise de son du démarrage ou de la reprise : micro (+ son de Teams partagé si l'option est choisie). `partageNeuf` :
   * son de Teams tout juste partagé (relance), pas encore dans l'état.
   */
  const preparer = React.useCallback(async (partageNeuf?: MediaStream): Promise<PriseDeSon> => {
    const flux = pisteVivante(micro.flux.current) ? micro.flux.current : await ouvrirMicroLibre(micro.choisi || undefined);
    if (!flux) throw new ErreurApi('interne', 0);
    const ecran = partageNeuf ?? (distanceActive && pisteVivante(partageRef.current) ? partageRef.current : null);
    const c = new PriseDeSon(flux, ecran);
    if (ecran && !c.mixe) {
      c.fermer(false);
      setErreurPartage('melange');
      throw new ErreurApi('interne', 0);
    }
    return c;
  }, [micro.flux, micro.choisi, ouvrirMicroLibre, distanceActive]);

  const demarrer = React.useCallback(async (corps: ReunionNouvelle, partageNeuf?: MediaStream) => {
    if (!format) throw new ErreurApi('interne', 0);
    if (await verrouTenu()) { setAilleurs(true); throw new ErreurApi('conflit', 409); }
    const c = await preparer(partageNeuf);
    try {
      const r = await contexte.current.api.post<Reunion>('/v1/outils/scribe/reunions', corps);
      capture.current = c;
      await moteur.demarrer({ reunion: r.id, titre: r.titre, utilisateur: moi.id, flux: c.sortie!, format, distance: c.mixe });
      void contexte.current.queryClient.invalidateQueries({ queryKey: ['scribe'] });
      return r;
    } catch (e) {
      if (capture.current === c) capture.current = null;
      c.fermer(false);
      throw e;
    }
  }, [format, preparer, moteur, moi.id]);

  const relancer = React.useCallback(async (corps: ReunionNouvelle) => {
    let partageNeuf: MediaStream | undefined;
    if (partagePossible && moteur.lire().reunion?.distance) {
      // Premier appel du geste de l'utilisateur : fenêtre de partage du navigateur.
      setErreurPartage(null);
      try {
        partageNeuf = await partagerSon();
      } catch (e) {
        setErreurPartage(e instanceof ErreurPartage ? e.motif : 'refuse');
        throw e;
      }
      arreterFlux(partageRef.current);
      partageRef.current = partageNeuf;
      setPartage(partageNeuf);
      setDistanceActive(true);
    }
    return demarrer(corps, partageNeuf);
  }, [partagePossible, moteur, demarrer]);

  const reprendre = React.useCallback(async () => {
    const e = moteur.lire();
    if (e.phase !== 'a-reprendre' || !e.reunion || !format) return;
    try {
      const r = await contexte.current.api.get<Reunion>(`/v1/outils/scribe/reunions/${encodeURIComponent(e.reunion.id)}`);
      if (r.etat !== 'enregistrement') { await moteur.oublier('reunion'); return; }
    } catch (err) {
      if (classerErreur(err) === 'reunion') { await moteur.oublier('reunion'); return; }
      // Réseau coupé : on reprend quand même, les parties attendront.
    }
    if (await verrouTenu()) { setAilleurs(true); return; }
    let c: PriseDeSon;
    try { c = await preparer(); } catch { return; }
    capture.current = c;
    await moteur.reprendre(c.sortie!, format, c.mixe);
    // Reprise impossible (durée maximale atteinte, réunion déjà terminée) : la prise de son est rendue.
    if (moteur.lire().phase !== 'enregistrement' && capture.current === c) { capture.current = null; c.fermer(false); }
  }, [moteur, format, preparer]);

  const fermerMicro = micro.fermer;
  const distance = React.useMemo<DistanceScribe | null>(() => (partagePossible ? {
    actif: distanceActive,
    partage: pisteVivante(partage),
    erreur: erreurPartage,
    activer: (on) => {
      setDistanceActive(on);
      setErreurPartage(null);
      if (!on) arreterPartage();
    },
    partager,
    arreter: arreterPartage,
  } : null), [partagePossible, distanceActive, partage, erreurPartage, partager, arreterPartage]);

  const valeur = React.useMemo<ContexteScribe | null>(() => (autorise ? {
    etat, ailleurs: (ailleurs || !!autreOnglet) && !enregistre, autreOnglet: enregistre ? null : autreOnglet,
    arreterAutreOnglet: () => { if (!enregistre && autreOnglet?.reunion) poster({ type: 'arreter', reunion: autreOnglet.reunion.id }); },
    format, persistant: moteur.persistant, distance, avisSon,
    micro: {
      flux: micro.etat.flux, appareils: micro.etat.appareils, choisi: micro.choisi, erreur: micro.etat.erreur, ouvrir: ouvrirMicro,
      // Panneau fermé hors enregistrement : le micro est rendu ; un partage du son de Teams reste actif (le navigateur
      // l'affiche, « Arrêter le partage » l'arrête) : la fenêtre de choix du partage ne doit pas le couper.
      fermer: () => { if (!capture.current) fermerMicro(); },
    },
    demarrer, relancer, reprendre,
    arreter: () => { void moteur.arreter(); },
    reprendreSon: () => moteur.reprendreSon(),
    terminer: () => { void moteur.terminerReprise(); },
    nouvelle: () => { moteur.effacer(); void moteur.charger(moi.id); },
  } : null), [
    autorise, etat, ailleurs, autreOnglet, poster, enregistre, format, moteur, distance, avisSon, micro.etat, micro.choisi, ouvrirMicro, fermerMicro,
    demarrer, relancer, reprendre, moi.id,
  ]);

  return <Ctx.Provider value={valeur}>{children}</Ctx.Provider>;
}

interface EtatMicro { flux: MediaStream | null; appareils: { id: string; libelle: string }[]; erreur: ErreurMicro | null }

/**
 * Micro choisi : ouverture (autorisation du navigateur), liste des micros, fermeture hors enregistrement. `prefere` :
 * micro choisi par l'utilisateur ('' = celui du système), repris quand le micro de l'enregistrement est perdu.
 */
function useMicro(enregistre: boolean) {
  const [etat, setEtat] = React.useState<EtatMicro>({ flux: null, appareils: [], erreur: null });
  const [choisi, setChoisi] = React.useState('');
  const flux = React.useRef<MediaStream | null>(null);
  const prefere = React.useRef('');
  const enCours = React.useRef(enregistre);
  enCours.current = enregistre;

  const actualiser = React.useCallback(async () => {
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    const appareils = (await md?.enumerateDevices().catch(() => [] as MediaDeviceInfo[]) ?? [])
      .filter((d) => d.kind === 'audioinput')
      .map((d) => ({ id: d.deviceId, libelle: d.label }));
    setEtat((e) => ({ ...e, appareils }));
    return appareils;
  }, []);

  /** Flux d'un micro (`id`, sinon celui du système), sans le retenir ; null (et l'erreur) si refusé ou absent. */
  const ouvrirFlux = React.useCallback(async (id?: string, pendant = false): Promise<MediaStream | null> => {
    const md = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined;
    if (!md?.getUserMedia) { if (!pendant) setEtat((e) => ({ ...e, flux: null, erreur: 'indisponible' })); return null; }
    try {
      return await md.getUserMedia({
        audio: { ...(id ? { deviceId: { exact: id } } : {}), echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
    } catch (e) {
      if (!pendant) setEtat((x) => ({ ...x, flux: null, erreur: (e as { name?: string })?.name === 'NotAllowedError' ? 'refuse' : 'indisponible' }));
      return null;
    }
  }, []);

  /** Retient le flux comme micro courant (niveau affiché, micro choisi). */
  const adopter = React.useCallback((nouveau: MediaStream) => {
    flux.current = nouveau;
    setChoisi(nouveau.getAudioTracks()[0]?.getSettings?.().deviceId ?? '');
    setEtat((e) => ({ ...e, flux: nouveau, erreur: null }));
    void actualiser();
  }, [actualiser]);

  const ouvrir = React.useCallback(async (id?: string): Promise<MediaStream | null> => {
    if (enCours.current) return flux.current;
    if (id !== undefined) prefere.current = id;
    const nouveau = await ouvrirFlux(id);
    if (!nouveau) return null;
    if (flux.current && flux.current !== nouveau) flux.current.getTracks().forEach((t) => t.stop());
    adopter(nouveau);
    return nouveau;
  }, [ouvrirFlux, adopter]);

  const fermer = React.useCallback(() => {
    if (enCours.current) return;
    flux.current?.getTracks().forEach((t) => t.stop());
    flux.current = null;
    setEtat((e) => (e.flux ? { ...e, flux: null } : e));
  }, []);

  /** Flux rendu par le moteur (fin de l'enregistrement) : oublié sans être arrêté une seconde fois. */
  const abandonner = React.useCallback(() => {
    if (!flux.current || flux.current.getTracks().some((t) => t.readyState === 'live')) return;
    flux.current = null;
    setEtat((e) => ({ ...e, flux: null }));
  }, []);

  return { etat, choisi, flux, prefere, ouvrir, ouvrirFlux, adopter, actualiser, fermer, abandonner };
}
